// ---------------------------------------------------------------------------
// Domain Classifier — detects admin/dashboard intent from user messages.
//
// Scans the user message for domain-specific keywords to classify the intent
// and select appropriate AppSpec templates and prompt strategies.
// ---------------------------------------------------------------------------

export type AppDomain = 'commerce' | 'dashboard' | 'portfolio' | 'admin'

export interface DomainClassification {
  domain: AppDomain
  confidence: number
}

// ---- Keyword dictionaries per domain ----

const ADMIN_KEYWORDS = [
  'admin', '관리', '관리자', 'crud', 'mms', '결제관리', '재고', '정산',
  '가맹점', 'merchant', 'settlement', 'inventory', 'backoffice', 'back-office',
  '운영', 'operation', '관리 페이지', 'admin panel', 'admin page',
  '회원관리', 'user management', '주문관리', 'order management',
]

const DASHBOARD_KEYWORDS = [
  'dashboard', '대시보드', 'analytics', '분석', 'chart', '차트',
  'kpi', 'metrics', 'report', '리포트', '보고서', 'statistics', '통계',
  'monitoring', '모니터링', 'overview', '개요',
]

const COMMERCE_KEYWORDS = [
  'shop', 'store', '쇼핑', '상점', 'ecommerce', 'e-commerce', '커머스',
  'product', '상품', 'cart', '장바구니', 'checkout', '결제',
  'order', '주문', 'payment', '배송', 'delivery', 'catalog', '카탈로그',
]

const PORTFOLIO_KEYWORDS = [
  'portfolio', '포트폴리오', 'landing', '랜딩', 'blog', '블로그',
  'profile', '프로필', 'resume', '이력서', 'showcase', 'gallery',
  'personal', '개인', 'about me',
]

// ---- Classification ----

function countKeywordMatches(text: string, keywords: string[]): number {
  const lower = text.toLowerCase()
  let count = 0
  for (const kw of keywords) {
    if (lower.includes(kw.toLowerCase())) count++
  }
  return count
}

/**
 * Classify the domain of a user message.
 *
 * Returns the best-matching domain with a confidence score (0-1).
 * Falls back to 'commerce' with low confidence if no keywords match.
 */
export function classifyDomain(message: string): DomainClassification {
  const scores: Record<AppDomain, number> = {
    admin: countKeywordMatches(message, ADMIN_KEYWORDS),
    dashboard: countKeywordMatches(message, DASHBOARD_KEYWORDS),
    commerce: countKeywordMatches(message, COMMERCE_KEYWORDS),
    portfolio: countKeywordMatches(message, PORTFOLIO_KEYWORDS),
  }

  const totalMatches = Object.values(scores).reduce((a, b) => a + b, 0)

  // No matches — default to commerce
  if (totalMatches === 0) {
    return { domain: 'commerce', confidence: 0.2 }
  }

  // Find the domain with the highest score
  let bestDomain: AppDomain = 'commerce'
  let bestScore = 0
  for (const [domain, score] of Object.entries(scores) as [AppDomain, number][]) {
    if (score > bestScore) {
      bestScore = score
      bestDomain = domain
    }
  }

  // Confidence: ratio of best domain's matches to total, scaled
  const confidence = Math.min(1, 0.5 + (bestScore / totalMatches) * 0.5)

  return { domain: bestDomain, confidence }
}
